import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Camera, MapPin, AlertTriangle, CheckCircle2,
  Trash2, Upload, Image as ImageIcon,
} from 'lucide-react';
import { api } from '@/utils/api';

// Photo capture + gallery for a service ticket.
// Norman's rules:
// - Phase label is PROMINENTLY shown (large pill at top).
// - If geolocation is denied and assignmentMode === 'agent', we show a
//   persistent red banner; the upload button becomes disabled.
// - Per-upload status is shown inline (progress, success, error).

type Phase = 'start' | 'finish' | 'component' | 'signature';

interface ServicePhoto {
  id: string;
  url: string;
  phase: Phase;
  isGeoTagged: boolean;
  latitude?: number | null;
  longitude?: number | null;
  gpsAccuracy?: number | null;
  capturedAt: string;
  source: string;
}

interface TicketLite {
  id: string;
  number: string;
  assignmentMode?: string;
  sopStage?: string;
  finishedAt?: string | null;
}

const PHASE_CONFIG: Record<Phase, { label: string; color: string; desc: string }> = {
  start: { label: 'Start Photos', color: 'bg-blue-600', desc: 'Taken at arrival, before work begins' },
  finish: { label: 'Finish Photos', color: 'bg-green-600', desc: '4–6 mandatory, taken at completion' },
  component: { label: 'Component Photos', color: 'bg-purple-600', desc: 'Parts installed or replaced' },
  signature: { label: 'Signature', color: 'bg-gray-600', desc: 'Customer signature capture' },
};

interface GeoState {
  status: 'idle' | 'pending' | 'ok' | 'denied' | 'unavailable';
  lat?: number;
  lng?: number;
  accuracy?: number;
  error?: string;
}

export function ServicePhotosPage() {
  const { id: ticketId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState<TicketLite | null>(null);
  const [photos, setPhotos] = useState<ServicePhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<Phase>('start');
  const [geo, setGeo] = useState<GeoState>({ status: 'idle' });
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchData = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      const [tRes, pRes] = await Promise.all([
        api.get(`/service-tickets/${ticketId}`),
        api.get(`/service/tickets/${ticketId}/photos`),
      ]);
      const t = tRes.data.data?.ticket ?? tRes.data.data ?? tRes.data;
      setTicket(t);
      const pData = pRes.data.data?.photos ?? pRes.data.data ?? [];
      setPhotos(Array.isArray(pData) ? pData : []);
    } catch {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-detect phase based on ticket lifecycle
  useEffect(() => {
    if (!ticket) return;
    if (ticket.finishedAt) setPhase('finish');
    else if (ticket.sopStage === 'on_site' || ticket.sopStage === 'post_repair') setPhase('finish');
  }, [ticket]);

  const requestGeo = useCallback(() => {
    if (!navigator.geolocation) {
      setGeo({ status: 'unavailable', error: 'Device has no GPS' });
      return;
    }
    setGeo({ status: 'pending' });
    navigator.geolocation.getCurrentPosition(
      pos => {
        setGeo({
          status: 'ok',
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      err => {
        setGeo({
          status: err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable',
          error: err.message,
        });
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
    );
  }, []);

  const isAgent = ticket?.assignmentMode === 'agent';
  const geoRequired = isAgent;
  const geoBlocking = geoRequired && (geo.status === 'denied' || geo.status === 'unavailable');

  const handleFileSelect = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const files = ev.target.files;
    if (!files || !ticketId) return;

    if (geoRequired && geo.status === 'idle') {
      setUploadErr('Get your location before uploading. Tap "Use my location" above.');
      return;
    }
    if (geoBlocking) {
      setUploadErr('Location permission denied. Cannot upload without GPS.');
      return;
    }
    if (geoRequired && geo.accuracy && geo.accuracy > 100) {
      setUploadErr(`GPS accuracy is ${Math.round(geo.accuracy)}m. Need < 100m. Move to better signal.`);
      return;
    }

    setUploading(true);
    setUploadErr(null);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('photo', file);
        fd.append('phase', phase);
        fd.append('source', 'app');
        if (geo.status === 'ok') {
          fd.append('latitude', String(geo.lat));
          fd.append('longitude', String(geo.lng));
          fd.append('gpsAccuracy', String(geo.accuracy));
        }
        await api.post(`/service/tickets/${ticketId}/photos`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      fetchData();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Upload failed';
      setUploadErr(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId: string) => {
    if (!confirm('Delete this photo? (Only allowed before ticket is finished.)')) return;
    try {
      await api.delete(`/service/photos/${photoId}`);
      fetchData();
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Cannot delete';
      alert(msg);
    }
  };

  const byPhase = (p: Phase) => photos.filter(x => x.phase === p);
  const finishCount = byPhase('finish').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/service-tickets/${ticketId}`)}
          className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Service Photos</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Ticket {ticket?.number || ''} — geo-tagged, phase-bucketed.
          </p>
        </div>
      </div>

      {/* Phase selector — the PROMINENT label Norman insists on */}
      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Uploading as
        </div>
        <div className={`mb-1 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-lg font-bold text-white ${PHASE_CONFIG[phase].color}`}>
          <Camera size={18} />
          {PHASE_CONFIG[phase].label}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">{PHASE_CONFIG[phase].desc}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {(Object.keys(PHASE_CONFIG) as Phase[]).map(p => (
            <button
              key={p}
              onClick={() => setPhase(p)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                phase === p
                  ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)] dark:bg-[var(--ink-0)] dark:text-[var(--saffron-600)]'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
              }`}
            >
              {PHASE_CONFIG[p].label} ({byPhase(p).length})
            </button>
          ))}
        </div>
      </div>

      {/* Geo status */}
      {geoRequired && (
        <div
          className={`mb-4 rounded-lg border p-3 text-sm ${
            geoBlocking
              ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-300'
              : geo.status === 'ok'
                ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300'
                : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300'
          }`}
        >
          <div className="flex items-start gap-2">
            {geoBlocking ? <AlertTriangle size={18} /> : geo.status === 'ok' ? <MapPin size={18} /> : <MapPin size={18} />}
            <div className="flex-1">
              {geo.status === 'idle' && (
                <>
                  <div className="font-semibold">Location required</div>
                  <div className="text-xs">On-role agents must capture GPS before uploading.</div>
                </>
              )}
              {geo.status === 'pending' && <div>Getting location…</div>}
              {geo.status === 'ok' && (
                <>
                  <div className="font-semibold">Location locked</div>
                  <div className="text-xs">
                    {geo.lat?.toFixed(5)}, {geo.lng?.toFixed(5)} · accuracy {Math.round(geo.accuracy || 0)}m
                  </div>
                </>
              )}
              {geo.status === 'denied' && (
                <>
                  <div className="font-semibold">Location permission denied</div>
                  <div className="text-xs">Enable location in browser/device settings, then retry.</div>
                </>
              )}
              {geo.status === 'unavailable' && (
                <>
                  <div className="font-semibold">GPS unavailable</div>
                  <div className="text-xs">{geo.error}</div>
                </>
              )}
            </div>
            {geo.status !== 'ok' && (
              <button
                onClick={requestGeo}
                className="rounded-md bg-white px-3 py-1 text-xs font-semibold text-gray-800 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-700"
              >
                {geo.status === 'pending' ? 'Locating…' : 'Use my location'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Upload area */}
      <div className="mb-6 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-900">
        <Upload size={32} className="mx-auto mb-2 text-gray-400" />
        <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Upload {PHASE_CONFIG[phase].label.toLowerCase()}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || geoBlocking}
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[var(--ink-1)] disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
          {uploading ? 'Uploading…' : 'Choose / Capture'}
        </button>
        {uploadErr && (
          <div className="mt-3 text-xs text-red-600 dark:text-red-400">{uploadErr}</div>
        )}
        {phase === 'finish' && (
          <div className={`mt-3 text-xs font-medium ${finishCount >= 4 ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
            {finishCount} / 4 minimum ({Math.max(0, 4 - finishCount)} more required)
          </div>
        )}
      </div>

      {/* Gallery, per phase */}
      <div className="space-y-5">
        {(Object.keys(PHASE_CONFIG) as Phase[]).map(p => {
          const items = byPhase(p);
          if (items.length === 0) return null;
          return (
            <div key={p} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <span className={`h-2 w-2 rounded-full ${PHASE_CONFIG[p].color}`} />
                  {PHASE_CONFIG[p].label}
                  <span className="text-xs font-normal text-gray-500">({items.length})</span>
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {items.map(photo => (
                  <PhotoTile key={photo.id} photo={photo} onDelete={() => handleDelete(photo.id)} canDelete={!ticket?.finishedAt} />
                ))}
              </div>
            </div>
          );
        })}
        {photos.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
            <ImageIcon size={28} className="mx-auto mb-2 text-gray-300" />
            No photos uploaded yet.
          </div>
        )}
      </div>
    </div>
  );
}

function PhotoTile({ photo, onDelete, canDelete }: { photo: ServicePhoto; onDelete: () => void; canDelete: boolean }) {
  return (
    <div className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <img
        src={photo.url}
        alt={`${photo.phase} photo`}
        className="aspect-square w-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-white">
        <div className="flex items-center justify-between text-[10px]">
          <span className="inline-flex items-center gap-1">
            {photo.isGeoTagged ? <MapPin size={10} /> : null}
            {photo.source === 'whatsapp' ? 'WhatsApp' : 'App'}
          </span>
          {!photo.isGeoTagged && photo.source !== 'whatsapp' && (
            <span className="inline-flex items-center gap-0.5 rounded bg-amber-500 px-1 text-[9px] font-bold">
              NO GPS
            </span>
          )}
        </div>
      </div>
      {canDelete && (
        <button
          onClick={onDelete}
          className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-600"
          aria-label="Delete"
        >
          <Trash2 size={12} />
        </button>
      )}
      {photo.isGeoTagged && photo.gpsAccuracy && photo.gpsAccuracy <= 100 && (
        <div className="absolute left-1 top-1 rounded bg-green-500/90 px-1.5 py-0.5 text-[9px] font-bold text-white">
          <CheckCircle2 size={10} className="inline" /> GPS
        </div>
      )}
    </div>
  );
}
